// src/pages/public/InscriptionPublique.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { APP_CONFIG } from '../../config/appConfig';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle2, AlertCircle, Mail, Phone, Globe2 } from 'lucide-react';

// ==========================================================================
// Composants d'alerte et badge
// ==========================================================================
const Alert = ({ type = 'info', children }) => {
    const styles = {
        info: 'bg-blue-50 border-blue-100 text-blue-900',
        success: 'bg-emerald-50 border-emerald-100 text-emerald-900',
        danger: 'bg-red-50 border-red-100 text-red-700',
        warning: 'bg-amber-50 border-amber-100 text-amber-800'
    };
    return (
        <div className={`border rounded-xl px-3 py-2 sm:px-4 sm:py-3 text-xs sm:text-sm ${styles[type]}`}>
            {children}
        </div>
    );
};

const Badge = ({ children, className = '' }) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-[11px] font-semibold ${className}`}>
        {children}
    </span>
);

// ==========================================================================
// Fonctions de chargement
// ==========================================================================
const loadFormationById = async (clientCode, id, ignore) => {
    try {
        const res = await fetch(
            `${APP_CONFIG.BASE_URL}/api/v1/public/formation/${id}?clientCode=${encodeURIComponent(clientCode)}`
        );
        if (res.ok) {
            const data = await res.json();
            if (!ignore) return data;
        }
    } catch (err) {
        console.error('Erreur chargement formation par ID:', err);
    }
    return null;
};

const loadEvaluationById = async (clientCode, id, ignore) => {
    try {
        const res = await fetch(
            `${APP_CONFIG.BASE_URL}/api/v1/public/evaluation/${id}?clientCode=${encodeURIComponent(clientCode)}`
        );
        if (res.ok) {
            const data = await res.json();
            if (!ignore) return data;
        }
    } catch (err) {
        console.error('Erreur chargement évaluation classique par ID:', err);
    }
    return null;
};

// Charger une évaluation enrichie par ID (privée)
const loadEvaluationRichById = async (clientCode, id, ignore) => {
    try {
        const res = await fetch(
            `${APP_CONFIG.BASE_URL}/api/v1/public/evaluationrich/${id}?clientCode=${encodeURIComponent(clientCode)}`
        );
        if (res.ok) {
            const data = await res.json();
            if (!ignore) return data;
        }
    } catch (err) {
        console.error('Erreur chargement évaluation enrichie par ID:', err);
    }
    return null;
};

// ==========================================================================
// Composant SelectableItemList
// ==========================================================================
const SelectableItemList = React.memo(function SelectableItemList({
    label, items, selectedIds, alreadyEnrolledIds, onToggle, onToggleAll, emptyLabel, typeBadgeLabel
}) {
    return (
        <div>
            <div className="flex justify-between items-center mb-3">
                <label className="text-xs sm:text-sm font-semibold text-gray-700">
                    {label} ({items.length})
                </label>
                {items.length > 1 && (
                    <button
                        type="button"
                        onClick={onToggleAll}
                        className="text-[10px] sm:text-xs text-blue-600 font-semibold hover:underline"
                    >
                        {selectedIds.length === items.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                    </button>
                )}
            </div>
            {items.length === 0 ? (
                <p className="text-xs sm:text-sm text-gray-400 italic">{emptyLabel}</p>
            ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                    {items.map(entry => {
                        const isSelected = selectedIds.includes(entry.id);
                        const isDejaInscrit = alreadyEnrolledIds.includes(entry.id);
                        return (
                            <label
                                key={entry.id}
                                className={`flex items-center gap-3 sm:gap-4 p-2 sm:p-3 border-2 rounded-xl cursor-pointer transition ${
                                    isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                                } ${isDejaInscrit ? 'opacity-60 cursor-not-allowed' : ''}`}
                            >
                                <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => onToggle(entry.id)}
                                    disabled={isDejaInscrit}
                                    className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 rounded focus:ring-blue-500"
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm sm:text-base font-semibold text-gray-800">
                                        {entry.titre}
                                        {isDejaInscrit && <span className="ml-2 text-[10px] sm:text-xs text-emerald-600 font-normal">✅ Déjà inscrit</span>}
                                        {!isDejaInscrit && <span className="ml-2 text-[10px] sm:text-xs text-gray-400 font-normal">📝 Inscription disponible</span>}
                                    </div>
                                    {entry.description && (
                                        <div className="text-xs sm:text-sm text-gray-500 truncate">{entry.description}</div>
                                    )}
                                </div>
                                <Badge className="bg-gray-100 text-gray-600">{entry.type || typeBadgeLabel}</Badge>
                                {entry.publique && (
                                    <Badge className="bg-emerald-50 text-emerald-700">
                                        <Globe2 size={11} className="mr-1" /> Public
                                    </Badge>
                                )}
                            </label>
                        );
                    })}
                </div>
            )}
        </div>
    );
});

// ==========================================================================
// Regex
// ==========================================================================
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MOBILE_REGEX = /^[0-9+\s().-]{8,20}$/;

// ==========================================================================
// Composant principal
// ==========================================================================
export default function InscriptionPublique() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Paramètres généraux
    const initialMode = searchParams.get('mode') || 'demander';
    const initialClientCode = searchParams.get('code') || '';
    const initialEmail = searchParams.get('email') || '';
    const initialKey = searchParams.get('KEY') || null;

    // ============================================================
    // ÉTATS RÉSOLUS (après décodage de la clé ou fallback)
    // ============================================================
    const [resolvedMode, setResolvedMode] = useState(initialMode);
    const [resolvedClientCode, setResolvedClientCode] = useState(initialClientCode);
    const [resolvedEmail, setResolvedEmail] = useState(initialEmail);
    const [resolvedTargetType, setResolvedTargetType] = useState(null);
    const [resolvedTargetId, setResolvedTargetId] = useState(null);
    const [paramsLoading, setParamsLoading] = useState(!!initialKey);

    // ============================================================
    // DÉCODAGE DE LA CLÉ (si KEY présent)
    // ============================================================
    useEffect(() => {
        const decodeKey = async () => {
            if (!initialKey) {
                // Pas de clé : on extrait les paramètres de l'URL
                const { targetType, targetId } = extractTargetFromParams(searchParams);
                setResolvedMode(initialMode);
                setResolvedClientCode(initialClientCode);
                setResolvedEmail(initialEmail);
                setResolvedTargetType(targetType);
                setResolvedTargetId(targetId);
                setParamsLoading(false);
                return;
            }

            try {
                const response = await fetch(
                    `${APP_CONFIG.BASE_URL}/api/v1/public/decodekey?key=${encodeURIComponent(initialKey)}`
                );
                if (!response.ok) {
                    const errData = await response.json().catch(() => ({}));
                    throw new Error(errData.error || 'Clé d\'invitation invalide ou expirée');
                }
                const data = await response.json();

                // Récupération des paramètres depuis le Map retourné
                const mode = data.mode || 'postuler';
                const code = data.code || '';
                const email = data.email || '';

                // Déterminer le type ciblé
                let targetType = null;
                let targetId = null;
                if (data.formationId) {
                    targetType = 'FORMATION';
                    targetId = parseInt(data.formationId, 10);
                } else if (data.evaluationId) {
                    targetType = 'EVALUATION';
                    targetId = parseInt(data.evaluationId, 10);
                } else if (data.evaluationRichId) {
                    targetType = 'EVALUATION_RICH';
                    targetId = parseInt(data.evaluationRichId, 10);
                } else if (data.type && data.id) {
                    targetType = data.type === 'EVALUATION' ? 'EVALUATION' :( data.type === 'FORMATION'?'FORMATION':'EVALUATION_RICH');
                    targetId = parseInt(data.id, 10);
                }

                setResolvedMode(mode);
                setResolvedClientCode(code);
                setResolvedEmail(email);
                setResolvedTargetType(targetType);
                setResolvedTargetId(targetId);
                setParamsLoading(false);
            } catch (err) {
                setError("Erreur de décodage de l'invitation : " + err.message);
                setParamsLoading(false);
                // On peut rediriger ou afficher une erreur bloquante
            }
        };
        decodeKey();
    }, [initialKey, searchParams]);

    // Fonction utilitaire pour extraire targetType et targetId depuis les paramètres
    function extractTargetFromParams(params) {
        const formationId = params.get('formationId') ? parseInt(params.get('formationId'), 10) : null;
        const evaluationId = params.get('evaluationId') ? parseInt(params.get('evaluationId'), 10) : null;
        const evaluationRichId = params.get('evaluationRichId') ? parseInt(params.get('evaluationRichId'), 10) : null;
        const legacyType = params.get('type') || '';
        const legacyId = params.get('id') ? parseInt(params.get('id'), 10) : null;

        if (formationId !== null) return { targetType: 'FORMATION', targetId: formationId };
        if (evaluationId !== null) return { targetType: 'EVALUATION', targetId: evaluationId };
        if (evaluationRichId !== null) return { targetType: 'EVALUATION_RICH', targetId: evaluationRichId };
        if (legacyType && legacyId !== null) {
            return { targetType: legacyType === 'EVALUATION' ? 'EVALUATION' : 'FORMATION', targetId: legacyId };
        }
        return { targetType: null, targetId: null };
    }

    // ============================================================
    // ÉTATS DU FORMULAIRE ET DES LISTES
    // ============================================================
    const [formData, setFormData] = useState({
        prenom: '',
        nom: '',
        email: resolvedEmail,
        mobile: '',
        dateNaissance: ''
    });

    // Mise à jour du formulaire quand l'email résolu change (après décodage)
    useEffect(() => {
        setFormData(prev => ({ ...prev, email: resolvedEmail }));
    }, [resolvedEmail]);

    // Code client (avec debounce)
    const [clientCode, setClientCode] = useState(resolvedClientCode);
    const [debouncedClientCode, setDebouncedClientCode] = useState(resolvedClientCode);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedClientCode(clientCode), 400);
        return () => clearTimeout(t);
    }, [clientCode]);

    // Formations
    const [formations, setFormations] = useState([]);
    const [inscritesFormations, setInscritesFormations] = useState([]);
    const [selectedFormations, setSelectedFormations] = useState(() => {
        if (resolvedTargetType === 'FORMATION') {
            return resolvedTargetId ? [resolvedTargetId] : [];
        }
        return [];
    });

    // Évaluations classiques
    const [evaluations, setEvaluations] = useState([]);
    const [inscritesEvaluations, setInscritesEvaluations] = useState([]);
    const [selectedEvaluations, setSelectedEvaluations] = useState(() => {
        if (resolvedTargetType === 'EVALUATION') {
            return resolvedTargetId ? [resolvedTargetId] : [];
        }
        return [];
    });

    // Évaluations enrichies
    const [evaluationsRich, setEvaluationsRich] = useState([]);
    const [inscritesEvaluationsRich, setInscritesEvaluationsRich] = useState([]);
    const [selectedEvaluationsRich, setSelectedEvaluationsRich] = useState(() => {
        if (resolvedTargetType === 'EVALUATION_RICH') {
            return resolvedTargetId ? [resolvedTargetId] : [];
        }
        return [];
    });

    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [listError, setListError] = useState(null);
    const [identityCheck, setIdentityCheck] = useState(null);
    const [showRegistrationForm, setShowRegistrationForm] = useState(false);
    const [isChecking, setIsChecking] = useState(false);

    const checkRequestRef = useRef(0);

	useEffect(() => {
	  if (resolvedClientCode) {
	    setClientCode(resolvedClientCode);
	    setDebouncedClientCode(resolvedClientCode);
	  }
	}, [resolvedClientCode]);
	
    // ============================================================
    // CHARGEMENT DES DONNÉES (dépend de resolvedMode, debouncedClientCode, resolvedTargetType, resolvedTargetId)
    // ============================================================
    useEffect(() => {
        if (paramsLoading) return; // attendre que les paramètres soient résolus

        let ignore = false;

        const run = async () => {
            setListError(null);
            try {
                const mode = resolvedMode;
                const clientCode = debouncedClientCode;
                const targetType = resolvedTargetType;
                const targetId = resolvedTargetId;

                if (mode === 'demander') {
                    await Promise.all([
                        loadFormationsPublics(ignore),
                        loadEvaluationsPublics(ignore),
                        loadEvaluationsRichPublics(ignore)
                    ]);
                } else if (mode === 'postuler' && clientCode && clientCode.length >= 3) {
                    if (targetId !== null) {
                        // Invitation ciblée
                        if (targetType === 'FORMATION') {
                            const formation = await loadFormationById(clientCode, targetId, ignore);
                            if (formation) {
                                setFormations([formation]);
                                setSelectedFormations([targetId]);
                            } else {
                                setListError("La formation invitée n'est pas disponible.");
                            }
                            setEvaluations([]);
                            setEvaluationsRich([]);
                        } else if (targetType === 'EVALUATION') {
                            const evalClassic = await loadEvaluationById(clientCode, targetId, ignore);
                            if (evalClassic) {
                                setEvaluations([evalClassic]);
                                setSelectedEvaluations([targetId]);
                            } else {
                                setListError("L'évaluation classique invitée n'est pas disponible.");
                            }
                            setFormations([]);
                            setEvaluationsRich([]);
                        } else if (targetType === 'EVALUATION_RICH') {
                            const evalRich = await loadEvaluationRichById(clientCode, targetId, ignore);
                            if (evalRich) {
                                setEvaluationsRich([{ ...evalRich, type: 'ÉVALUATION RICHE' }]);
                                setSelectedEvaluationsRich([targetId]);
                            } else {
                                setListError("L'évaluation enrichie invitée n'est pas disponible.");
                            }
                            setFormations([]);
                            setEvaluations([]);
                        }
                    } else {
                        // Pas de cible : on charge toutes les listes
                        await Promise.all([
                            loadFormationsParClient(clientCode, ignore),
                            loadEvaluationsParClient(clientCode, ignore),
                            loadEvaluationsRichParClient(clientCode, ignore)
                        ]);
                    }
                } else if (mode === 'postuler') {
                    setFormations([]);
                    setEvaluations([]);
                    setEvaluationsRich([]);
                }
            } catch (err) {
                if (!ignore) {
                    setListError("Impossible de charger les données. Réessayez.");
                    console.error(err);
                }
            }
        };
        run();
        return () => { ignore = true; };
    }, [resolvedMode, debouncedClientCode, resolvedTargetType, resolvedTargetId, paramsLoading]);

    // Synchronisation des sélections (on retire les IDs qui ne sont plus dans les listes)
    useEffect(() => {
        setSelectedFormations(prev => prev.filter(id => formations.some(f => f.id === id)));
    }, [formations]);

    useEffect(() => {
        setSelectedEvaluations(prev => prev.filter(id => evaluations.some(e => e.id === id)));
    }, [evaluations]);

    useEffect(() => {
        setSelectedEvaluationsRich(prev => prev.filter(id => evaluationsRich.some(e => e.id === id)));
    }, [evaluationsRich]);

    // Chargement des inscriptions existantes quand l'email est connu
    useEffect(() => {
        if (formData.email && identityCheck === true) {
            loadInscriptionsExistantesFormations(formData.email);
            loadInscriptionsExistantesEvaluations(formData.email);
            loadInscriptionsExistantesEvaluationsRich(formData.email);
        } else {
            setInscritesFormations([]);
            setInscritesEvaluations([]);
            setInscritesEvaluationsRich([]);
        }
    }, [formData.email, identityCheck]);

    // ============================================================
    // FONCTIONS DE CHARGEMENT (appels API)
    // ============================================================

    const loadFormationsPublics = async (ignore) => {
        const res = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/public/formationspublics`);
        if (res.ok) {
            const data = await res.json();
            if (!ignore) setFormations(data.data || data || []);
        }
    };

    const loadFormationsParClient = async (code, ignore) => {
        const res = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/public/formations?clientCode=${encodeURIComponent(code)}`);
        if (res.ok) {
            const data = await res.json();
            if (!ignore) setFormations(data.data || data || []);
        }
    };

    const loadEvaluationsPublics = async (ignore) => {
        const res = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/public/evaluationspublics`);
        if (res.ok) {
            const data = await res.json();
            if (!ignore) {
                const enriched = data.data || data || [];
                const withType = enriched.map(e => ({ ...e, type: 'ÉVALUATION CLASSIQUE' }));
                setEvaluations(withType);
            }
        }
    };

    const loadEvaluationsParClient = async (code, ignore) => {
        const res = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/public/evaluations?clientCode=${encodeURIComponent(code)}`);
        if (res.ok) {
            const data = await res.json();
            if (!ignore) {
                const enriched = data.data || data || [];
                const withType = enriched.map(e => ({ ...e, type: 'ÉVALUATION CLASSIQUE' }));
                setEvaluations(withType);
            }
        }
    };

    const loadEvaluationsRichPublics = async (ignore) => {
        const res = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/public/evaluationsrichpublics`);
        if (res.ok) {
            const data = await res.json();
            if (!ignore) {
                const enriched = data.data || data || [];
                const withType = enriched.map(e => ({ ...e, type: 'ÉVALUATION RICHE' }));
                setEvaluationsRich(withType);
            }
        }
    };

    const loadEvaluationsRichParClient = async (code, ignore) => {
        const res = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/public/evaluationsrich?clientCode=${encodeURIComponent(code)}`);
        if (res.ok) {
            const data = await res.json();
            if (!ignore) {
                const enriched = data.data || data || [];
                const withType = enriched.map(e => ({ ...e, type: 'ÉVALUATION RICHE' }));
                setEvaluationsRich(withType);
            }
        }
    };

    const loadInscriptionsExistantesFormations = async (email) => {
        try {
            const res = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/public/inscriptions?email=${encodeURIComponent(email)}`);
            if (res.ok) {
                const data = await res.json();
                setInscritesFormations(data.data || data || []);
            }
        } catch (err) {
            console.error('Erreur chargement inscriptions formations:', err);
        }
    };

    const loadInscriptionsExistantesEvaluations = async (email) => {
        try {
            const res = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/public/inscriptions-evaluations?email=${encodeURIComponent(email)}`);
            if (res.ok) {
                const data = await res.json();
                setInscritesEvaluations(data.data || data || []);
            }
        } catch (err) {
            console.error('Erreur chargement inscriptions évaluations classiques:', err);
        }
    };

    const loadInscriptionsExistantesEvaluationsRich = async (email) => {
        try {
            const res = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/public/inscriptions-evaluations-rich?email=${encodeURIComponent(email)}`);
            if (res.ok) {
                const data = await res.json();
                setInscritesEvaluationsRich(data.data || data || []);
            }
        } catch (err) {
            console.error('Erreur chargement inscriptions évaluations enrichies:', err);
        }
    };

    // ============================================================
    // VÉRIFICATION IDENTITÉ
    // ============================================================

    const checkIdentity = useCallback(async (overrides = {}) => {
        const email = overrides.email ?? formData.email;
        const mobile = overrides.mobile ?? formData.mobile;

        if (!email && !mobile) {
            setIdentityCheck(null);
            setShowRegistrationForm(false);
            return null;
        }

        const requestId = ++checkRequestRef.current;
        setIsChecking(true);
        try {
            const params = new URLSearchParams();
            if (email) params.append('email', email);
            if (mobile) params.append('mobile', mobile);
            const res = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/public/verifier?${params}`);

            if (requestId !== checkRequestRef.current) return null;

            if (!res.ok) throw new Error('Erreur lors de la vérification');
            const data = await res.json();
            const existe = !!data.existe;
            setIdentityCheck(existe);
            setShowRegistrationForm(!existe && !!email);
            if (existe && email) {
                await loadInscriptionsExistantesFormations(email);
                await loadInscriptionsExistantesEvaluations(email);
                await loadInscriptionsExistantesEvaluationsRich(email);
            }
            return existe;
        } catch (err) {
            console.error('Erreur vérification:', err);
            if (requestId === checkRequestRef.current) {
                setError("Impossible de vérifier votre identité pour le moment. Réessayez.");
            }
            return null;
        } finally {
            if (requestId === checkRequestRef.current) setIsChecking(false);
        }
    }, [formData.email, formData.mobile]);

    useEffect(() => {
        if (resolvedEmail) {
            checkIdentity({ email: resolvedEmail });
        }
    }, [resolvedEmail]);

    const handleEmailChange = (e) => {
        const value = e.target.value;
        setFormData(prev => ({ ...prev, email: value }));
        if (identityCheck !== null) {
            setIdentityCheck(null);
            setShowRegistrationForm(false);
        }
    };

    const handleMobileChange = (e) => {
        const value = e.target.value;
        setFormData(prev => ({ ...prev, mobile: value }));
        if (identityCheck !== null) {
            setIdentityCheck(null);
        }
    };

    // ============================================================
    // SOUMISSION
    // ============================================================

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setResult(null);

        if (!formData.email || !EMAIL_REGEX.test(formData.email)) {
            setError('Veuillez saisir une adresse email valide.');
            setLoading(false);
            return;
        }
        if (formData.mobile && !MOBILE_REGEX.test(formData.mobile)) {
            setError('Le numéro de téléphone saisi semble invalide.');
            setLoading(false);
            return;
        }
        if (resolvedMode === 'postuler' && (!clientCode || clientCode.trim().length < 3)) {
            setError('Veuillez saisir le code du client fourni par votre invitation.');
            setLoading(false);
            return;
        }

        let existe = identityCheck;
        if (existe === null) {
            existe = await checkIdentity();
        }

        if (!existe && (!formData.prenom || !formData.nom)) {
            setShowRegistrationForm(true);
            setError('Veuillez compléter vos informations (prénom, nom) pour créer votre compte.');
            setLoading(false);
            return;
        }

        const formationsToInscrire = selectedFormations.filter(id => !inscritesFormations.includes(id));
        const evaluationsToInscrire = selectedEvaluations.filter(id => !inscritesEvaluations.includes(id));
        const evaluationsRichToInscrire = selectedEvaluationsRich.filter(id => !inscritesEvaluationsRich.includes(id));

        if (formationsToInscrire.length === 0 && evaluationsToInscrire.length === 0 && evaluationsRichToInscrire.length === 0) {
            setError('Vous êtes déjà inscrit à toutes les formations/évaluations sélectionnées.');
            setLoading(false);
            return;
        }

        const payload = existe
            ? {
                  email: formData.email,
                  mobile: formData.mobile || undefined,
                  formationIds: formationsToInscrire,
                  evaluationIds: evaluationsToInscrire,
                  evaluationRichIds: evaluationsRichToInscrire,
                  mode: resolvedMode
              }
            : {
                  ...formData,
                  formationIds: formationsToInscrire,
                  evaluationIds: evaluationsToInscrire,
                  evaluationRichIds: evaluationsRichToInscrire,
                  mode: resolvedMode
              };
        if (resolvedMode === 'postuler') {
            payload.clientCode = clientCode;
        }

        const endpoint = resolvedMode === 'postuler' ? '/postuler' : '/demander';
        const url = `${APP_CONFIG.BASE_URL}/api/v1/public${endpoint}`;

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(data.message || 'Erreur lors de l\'envoi');
            }
            setResult(data);
            setSelectedFormations([]);
            setSelectedEvaluations([]);
            setSelectedEvaluationsRich([]);
            setTimeout(() => navigate('/'), 2500);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // ============================================================
    // GESTION DES SÉLECTIONS
    // ============================================================

    const toggleFormation = useCallback((id) => {
        setSelectedFormations(prev =>
            prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]
        );
    }, []);

    const toggleEvaluation = useCallback((id) => {
        setSelectedEvaluations(prev =>
            prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]
        );
    }, []);

    const toggleEvaluationRich = useCallback((id) => {
        setSelectedEvaluationsRich(prev =>
            prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]
        );
    }, []);

    const toggleAllFormations = useCallback(() => {
        setSelectedFormations(prev =>
            prev.length === formations.length ? [] : formations.map(f => f.id)
        );
    }, [formations]);

    const toggleAllEvaluations = useCallback(() => {
        setSelectedEvaluations(prev =>
            prev.length === evaluations.length ? [] : evaluations.map(e => e.id)
        );
    }, [evaluations]);

    const toggleAllEvaluationsRich = useCallback(() => {
        setSelectedEvaluationsRich(prev =>
            prev.length === evaluationsRich.length ? [] : evaluationsRich.map(e => e.id)
        );
    }, [evaluationsRich]);

    // ============================================================
    // AFFICHAGE CONDITIONNEL DES LISTES
    // ============================================================

    const isTargetedInvitation = resolvedMode === 'postuler' && resolvedTargetId !== null;

    let targetedList = null;
    let targetedLabel = '';
    let targetedSelected = [];
    let targetedInscrites = [];
    let targetedToggle = null;
    let targetedToggleAll = null;
    let targetedEmptyLabel = '';
    let targetedTypeBadge = '';

    if (isTargetedInvitation) {
        if (formations.length > 0) {
            targetedList = formations;
            targetedLabel = 'Formation invitée';
            targetedSelected = selectedFormations;
            targetedInscrites = inscritesFormations;
            targetedToggle = toggleFormation;
            targetedToggleAll = toggleAllFormations;
            targetedEmptyLabel = 'La formation invitée n\'est pas disponible.';
            targetedTypeBadge = 'FORMATION';
        } else if (evaluations.length > 0) {
            targetedList = evaluations;
            targetedLabel = 'Évaluation classique invitée';
            targetedSelected = selectedEvaluations;
            targetedInscrites = inscritesEvaluations;
            targetedToggle = toggleEvaluation;
            targetedToggleAll = toggleAllEvaluations;
            targetedEmptyLabel = 'L\'évaluation classique invitée n\'est pas disponible.';
            targetedTypeBadge = 'ÉVALUATION CLASSIQUE';
        } else if (evaluationsRich.length > 0) {
            targetedList = evaluationsRich;
            targetedLabel = 'Évaluation enrichie invitée';
            targetedSelected = selectedEvaluationsRich;
            targetedInscrites = inscritesEvaluationsRich;
            targetedToggle = toggleEvaluationRich;
            targetedToggleAll = toggleAllEvaluationsRich;
            targetedEmptyLabel = 'L\'évaluation enrichie invitée n\'est pas disponible.';
            targetedTypeBadge = 'ÉVALUATION RICHE';
        }
    }

    // ============================================================
    // RENDU
    // ============================================================

    const getModeLabel = () => {
        return resolvedMode === 'postuler'
            ? 'Postuler aux formations/évaluations sélectionnées'
            : 'Demander les formations/évaluations sélectionnées';
    };

    const totalSelected =
        selectedFormations.filter(id => !inscritesFormations.includes(id)).length +
        selectedEvaluations.filter(id => !inscritesEvaluations.includes(id)).length +
        selectedEvaluationsRich.filter(id => !inscritesEvaluationsRich.includes(id)).length;

    const mobileFormatValid = !formData.mobile || MOBILE_REGEX.test(formData.mobile);

    if (paramsLoading) {
        return (
            <div className="bg-gray-50 min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <Loader2 size={40} className="animate-spin text-blue-600 mx-auto" />
                    <p className="mt-4 text-gray-600">Décodage de l'invitation...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gray-50 min-h-screen py-6 sm:py-8 px-3 sm:px-4">
            <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
                {/* En-tête */}
                <div className="bg-emerald-800 text-white px-4 sm:px-6 py-4 sm:py-5 flex items-center gap-3 sm:gap-4 border-b-4 border-emerald-600">
                    <img src="/images/logoiss.png" alt="ISS Logo" className="h-9 sm:h-10 w-auto bg-white p-1 rounded" />
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold m-0 text-white">
                            🎓 Vos Inscriptions
                        </h1>
                        <p className="text-xs sm:text-sm text-white/80 m-0">
                            {resolvedMode === 'postuler'
                                ? 'Vous avez été invité à postuler à une formation ou évaluation'
                                : 'Découvrez les formations et évaluations disponibles'}
                        </p>
                    </div>
                </div>

                <div className="p-4 sm:p-6 space-y-4">
                    {resolvedMode === 'postuler' && (
                        <Alert type="info">
                            💡 Vous avez reçu une invitation. Sélectionnez les éléments auxquels vous souhaitez postuler. Cette inscription ne sera pas rendue publique.
                        </Alert>
                    )}

                    {listError && <Alert type="danger">{listError}</Alert>}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Code client (postuler) - visible uniquement si pas de KEY ou si code modifiable */}
                        {resolvedMode === 'postuler' && (
                            <div>
                                <label className="block text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Code du client *</label>
                                <input
                                    type="text"
                                    value={clientCode}
                                    onChange={(e) => setClientCode(e.target.value.toUpperCase())}
                                    placeholder="Entrez le code du client"
                                    className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    required
                                    disabled={!!initialKey} // si KEY présent, le code est fixé
                                />
                                <p className="text-[10px] sm:text-xs text-gray-400 mt-1">
                                    {initialKey ? "Code client fourni par l'invitation" : 'Saisissez le code fourni par votre client'}
                                </p>
                            </div>
                        )}

                        {/* Email */}
                        <div>
                            <label className="block text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Email *</label>
                            <div className="relative">
                                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={handleEmailChange}
                                    onBlur={() => checkIdentity()}
                                    placeholder="votre@email.com"
                                    className="w-full pl-9 pr-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    required
                                    disabled={!!initialKey} // si KEY présent, l'email est fixé
                                />
                            </div>
                            {isChecking && (
                                <div className="text-[10px] sm:text-xs text-gray-500 mt-1 flex items-center gap-1.5">
                                    <Loader2 size={12} className="animate-spin" /> Vérification en cours...
                                </div>
                            )}
                            {!isChecking && identityCheck !== null && (
                                <div className={`text-[10px] sm:text-xs font-medium mt-1 flex items-center gap-1.5 ${identityCheck ? 'text-emerald-600' : 'text-amber-600'}`}>
                                    {identityCheck ? <CheckCircle2 size={13} /> : <AlertCircle size={13} />}
                                    {identityCheck ? 'Compte existant' : 'Nouvel étudiant'}
                                </div>
                            )}
                            {initialKey && <p className="text-[10px] sm:text-xs text-gray-400 mt-1">Email fourni par l'invitation</p>}
                        </div>

                        {/* Formulaire d'inscription (nouveau compte) */}
                        {showRegistrationForm && (
                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 sm:p-4 space-y-4">
                                <p className="text-xs sm:text-sm font-semibold text-amber-800">
                                    📝 Complétez vos informations pour créer votre compte
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                    <div>
                                        <label className="block text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Prénom *</label>
                                        <input
                                            type="text"
                                            value={formData.prenom}
                                            onChange={(e) => setFormData(prev => ({ ...prev, prenom: e.target.value }))}
                                            placeholder="Votre prénom"
                                            className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Nom *</label>
                                        <input
                                            type="text"
                                            value={formData.nom}
                                            onChange={(e) => setFormData(prev => ({ ...prev, nom: e.target.value }))}
                                            placeholder="Votre nom"
                                            className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Téléphone</label>
                                        <div className="relative">
                                            <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                            <input
                                                type="tel"
                                                inputMode="tel"
                                                autoComplete="tel"
                                                value={formData.mobile}
                                                onChange={handleMobileChange}
                                                onBlur={() => checkIdentity()}
                                                placeholder="+225 07 00 00 00 00"
                                                aria-invalid={formData.mobile ? !mobileFormatValid : undefined}
                                                aria-describedby={formData.mobile && !mobileFormatValid ? 'mobile-error' : undefined}
                                                className={`w-full pl-9 pr-3 py-1.5 sm:py-2 text-xs sm:text-sm border rounded-lg focus:ring-2 focus:border-transparent ${
                                                    formData.mobile && !mobileFormatValid
                                                        ? 'border-red-300 focus:ring-red-500'
                                                        : 'border-gray-300 focus:ring-blue-500'
                                                }`}
                                            />
                                        </div>
                                        {formData.mobile && !mobileFormatValid && (
                                            <div id="mobile-error" className="text-[10px] sm:text-xs font-medium mt-1 flex items-center gap-1.5 text-red-600">
                                                <AlertCircle size={13} /> Numéro invalide (8 à 20 caractères : chiffres, espaces, +, (), -, .)
                                            </div>
                                        )}
                                        {isChecking && (
                                            <div className="text-[10px] sm:text-xs text-gray-500 mt-1 flex items-center gap-1.5">
                                                <Loader2 size={12} className="animate-spin" /> Vérification en cours...
                                            </div>
                                        )}
                                        {!isChecking && identityCheck !== null && (
                                            <div className={`text-[10px] sm:text-xs font-medium mt-1 flex items-center gap-1.5 ${identityCheck ? 'text-emerald-600' : 'text-amber-600'}`}>
                                                {identityCheck ? <CheckCircle2 size={13} /> : <AlertCircle size={13} />}
                                                {identityCheck ? 'Compte existant' : 'Nouvel étudiant'}
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Date de naissance</label>
                                        <input
                                            type="date"
                                            value={formData.dateNaissance || ''}
                                            onChange={(e) => setFormData(prev => ({ ...prev, dateNaissance: e.target.value }))}
                                            className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {identityCheck === true && formData.email && (
                            <Alert type="success">
                                ✅ Vous êtes déjà inscrit avec l'email : <strong>{formData.email}</strong>
                            </Alert>
                        )}

                        {/* ============================================================ */}
                        {/* AFFICHAGE DES LISTES : CONDITIONNEL SELON LE MODE               */}
                        {/* ============================================================ */}
                        {isTargetedInvitation ? (
                            // Mode invitation ciblée : on n'affiche que l'élément invité
                            targetedList && (
                                <div>
                                    <Alert type="info">
                                        Vous avez été invité à postuler à l'élément ci-dessous. Seul cet élément est disponible pour cette invitation.
                                    </Alert>
                                    <SelectableItemList
                                        label={targetedLabel}
                                        items={targetedList}
                                        selectedIds={targetedSelected}
                                        alreadyEnrolledIds={targetedInscrites}
                                        onToggle={targetedToggle}
                                        onToggleAll={targetedToggleAll}
                                        emptyLabel={targetedEmptyLabel}
                                        typeBadgeLabel={targetedTypeBadge}
                                    />
                                </div>
                            )
                        ) : (
                            // Mode demander ou postuler sans ID cible : on affiche toutes les listes
                            <>
                                <SelectableItemList
                                    label="Formations disponibles"
                                    items={formations}
                                    selectedIds={selectedFormations}
                                    alreadyEnrolledIds={inscritesFormations}
                                    onToggle={toggleFormation}
                                    onToggleAll={toggleAllFormations}
                                    emptyLabel={resolvedMode === 'postuler' && (!clientCode || clientCode.length < 3)
                                        ? 'Saisissez un code client valide pour voir les formations disponibles.'
                                        : 'Aucune formation publique disponible.'}
                                    typeBadgeLabel="FORMATION"
                                />

                                <SelectableItemList
                                    label="Évaluations classiques disponibles"
                                    items={evaluations}
                                    selectedIds={selectedEvaluations}
                                    alreadyEnrolledIds={inscritesEvaluations}
                                    onToggle={toggleEvaluation}
                                    onToggleAll={toggleAllEvaluations}
                                    emptyLabel={resolvedMode === 'postuler' && (!clientCode || clientCode.length < 3)
                                        ? 'Saisissez un code client valide pour voir les évaluations disponibles.'
                                        : 'Aucune évaluation classique publique disponible.'}
                                    typeBadgeLabel="ÉVALUATION CLASSIQUE"
                                />

                                <SelectableItemList
                                    label="Évaluations enrichies disponibles"
                                    items={evaluationsRich}
                                    selectedIds={selectedEvaluationsRich}
                                    alreadyEnrolledIds={inscritesEvaluationsRich}
                                    onToggle={toggleEvaluationRich}
                                    onToggleAll={toggleAllEvaluationsRich}
                                    emptyLabel={resolvedMode === 'postuler' && (!clientCode || clientCode.length < 3)
                                        ? 'Saisissez un code client valide pour voir les évaluations enrichies disponibles.'
                                        : 'Aucune évaluation enrichie publique disponible.'}
                                    typeBadgeLabel="ÉVALUATION RICHE"
                                />
                            </>
                        )}

                        <div className="text-xs sm:text-sm text-gray-500">
                            {totalSelected} élément(s) sélectionné(s) au total
                        </div>

                        {error && <Alert type="danger">❌ {error}</Alert>}
                        {result && (
                            <Alert type="success">
                                <div className="font-semibold">
                                    ✅ {result.nouveauCompte ? 'Compte créé et ' : ''}Demande envoyée avec succès !
                                </div>
                                <div className="text-xs sm:text-sm mt-1">{result.message}</div>
                                {result.inscriptions && result.inscriptions.length > 0 && (
                                    <div className="mt-2">
                                        <div className="text-xs sm:text-sm font-medium">Inscriptions demandées :</div>
                                        <ul className="text-xs sm:text-sm list-disc list-inside">
                                            {result.inscriptions.map((ins, idx) => (
                                                <li key={idx}>
                                                    {ins.formationTitre || 'Évaluation'}
                                                    <Badge className="ml-2 bg-amber-100 text-amber-700">
                                                        {ins.libelleStatut || 'En attente'}
                                                    </Badge>
                                                </li>
                                            ))}
                                        </ul>
                                        {result.emailEnvoye && (
                                            <div className="mt-2 text-[10px] sm:text-xs">
                                                📧 Un email de confirmation vous a été envoyé à <strong>{formData.email}</strong>.
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Alert>
                        )}

                        <div className="flex flex-col gap-3">
                            <button
                                type="submit"
                                disabled={loading || (formations.length === 0 && evaluations.length === 0 && evaluationsRich.length === 0) || totalSelected === 0 || !formData.email || !mobileFormatValid}
                                className="w-full py-2 sm:py-3 text-sm sm:text-base font-bold bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <Loader2 size={18} className="animate-spin" />
                                        Envoi en cours...
                                    </span>
                                ) : (
                                    getModeLabel()
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={() => navigate('/')}
                                className="w-full py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
                            >
                                ← Retour à l'accueil
                            </button>
                        </div>
                        <p className="text-[10px] sm:text-xs text-gray-400 text-center mt-2">
                            {resolvedMode === 'postuler'
                                ? 'Vous recevrez une confirmation par email. Votre demande sera examinée par le client.'
                                : 'Votre demande sera examinée par le client. Vous recevrez une réponse par email.'}
                        </p>
                    </form>
                </div>

                <div className="bg-gray-50 px-4 sm:px-6 py-3 text-center text-[10px] sm:text-xs text-gray-400 border-t border-gray-200">
                    © 2026 Ivoire Soft Services – Plateforme de Formation
                </div>
            </div>
        </div>
    );
}